/**
 * voice-v8 — grosse Tabellen im Chat-Antwortweg: ein Satz statt Zelle fuer Zelle
 * (Ticket 2942, Rest, 14.10.2026)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Martin, 02.10.2026: Tabellenwerte „werden nicht gesprochen, sie bleiben nur
 * sichtbar“. Ohne Sprechfassung ging eine Tabelle bisher Zeile fuer Zeile an
 * die Stimme. Ab `TABELLE_AB_DATENZEILEN` Datenzeilen wird sie zu EINEM Satz;
 * kleinere (drei Posten einer Rechnung, Ticket #1882; eine kurze Terminliste)
 * bleiben hoerbar.
 *
 * ⚠️ Eine reine Funktion in shared/, weil sie an ZWEI Stellen laufen muss und
 *    dieselbe Schwelle braucht:
 *      - im Browser, auf dem GANZEN Text, bevor die Warteschlange ihn in
 *        Saetze schneidet (frontend/app.ts: sprechNachschub, playTts). Danach
 *        waere keine Tabelle mehr beisammen, und kein Stueck erreichte die
 *        Schwelle;
 *      - am Server fuer die Chat-Wege (/api/tts/prepare mit `satz`,
 *        /api/voice-preview) – NICHT fuer Dokumente, Projekte, Meldungen,
 *        Telegram, Meditation: dort ist die Tabelle der Inhalt.
 *
 * Im Strom waechst der Text: Eine Tabelle, die noch klein ist, aber am Ende
 * des Textes steht, wird ZURUECKGEHALTEN (`fertig: false`) – sonst spraeche
 * die Stimme drei Zeilen, und erst die vierte machte die Tabelle gross. Der
 * zurueckgehaltene Text ist immer ein Anfang des spaeteren (der Zeiger in
 * `sprechNachschub` zaehlt Zeichen und darf nie zurueckspringen).
 */
export const TABELLE_IM_CHAT = "Die Tabelle steht im Chat.";
/** Ab so vielen Datenzeilen (ohne Kopf und Trennlinie) ist eine Tabelle gross. */
export const TABELLE_AB_DATENZEILEN = 4;

/**
 * Eine Tabellenzeile: beginnt mit „|“ und hat mindestens zwei Pipes. Zeilen mit Pipes mitten im Satz
 * (Shell-Befehl, Fliesstext) sind keine – sonst gingen im Strom Saetze verloren.
 */
function istBlockZeile(z: string): boolean {
  const t = z.trim();
  return t.startsWith("|") && t.split("|").length > 2;
}

function istTrennlinie(z: string): boolean {
  return /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(z) && z.includes("-");
}

/**
 * Bittet der Nutzer ausdruecklich darum, etwas vorzulesen? Dann bleibt die Tabelle hoerbar.
 * Bewusst weit: jedes „vorlesen“ und jedes „lies … vor“ zaehlt („Lies mir die Rechnung vor“,
 * „Kannst du mir die Posten vorlesen?“) – lieber einmal zu viel vorgelesen als eine Bitte uebergangen.
 */
export function bittetUmVorlesen(frage: string | undefined): boolean {
  const f = frage ?? "";
  return /vorles|vorlies|vorgelesen|(?<![\p{L}])(?:lies|lese|les)(?![\p{L}])[^.?!]*(?<![\p{L}])vor(?![\p{L}])|sag (?:mir|uns) die/iu.test(f);
}

export function grosseTabellenErsetzen(text: string, opt: { fertig?: boolean } = {}): string {
  const fertig = opt.fertig !== false;
  if (!text || !text.includes("|")) return text;
  const zeilen = text.split("\n");
  // Codebloecke (```) sind nie eine Tabelle: Shell-Zeilen mit Pipes bleiben, wie sie sind.
  const imCode: boolean[] = [];
  let offen = false;
  for (const z of zeilen) {
    if (/^\s*```/.test(z)) {
      offen = !offen;
      imCode.push(true);
    } else imCode.push(offen);
  }
  const laeuftNoch = (i: number): boolean =>
    !fertig && i === zeilen.length - 1 && !imCode[i] && (zeilen[i] ?? "").trim().startsWith("|");
  const blockZeile = (i: number): boolean => !imCode[i] && istBlockZeile(zeilen[i] ?? "");
  const raus: string[] = [];
  let zurueckgehalten = false;
  let satzOffen = false;
  for (let i = 0; i < zeilen.length; i++) {
    if (!blockZeile(i) && !laeuftNoch(i)) {
      raus.push(zeilen[i] ?? "");
      continue;
    }
    const block: string[] = [];
    for (; i < zeilen.length; i++) {
      if (!blockZeile(i) && !laeuftNoch(i)) break;
      block.push(zeilen[i] ?? "");
    }
    i--; // die Zeile, die den Block beendet hat, gehoert der aeusseren Schleife
    const daten = block.filter((b) => !istTrennlinie(b));
    if (daten.length - 1 >= TABELLE_AB_DATENZEILEN) {
      // Eine Anmoderation „Hier die Werte:“ bekommt ihren Punkt (gleiche Laenge: der Zeiger bleibt stimmig).
      let davor = raus.length - 1;
      while (davor >= 0 && !(raus[davor] ?? "").trim()) davor--;
      if (davor >= 0 && /:\s*$/.test(raus[davor] ?? "")) raus[davor] = (raus[davor] ?? "").replace(/:\s*$/, ".");
      raus.push(TABELLE_IM_CHAT);
      // Die Tabelle laeuft noch (nur Leeres dahinter): ihr Zeilenende gehoert schon zum Satz, sonst schrumpfte er mit der naechsten Zeile.
      if (!fertig && zeilen.slice(i + 1).every((r) => !r.trim())) satzOffen = true;
    } else if (!fertig && zeilen.slice(i + 1).every((r) => !r.trim())) {
      zurueckgehalten = true; // steht am Ende und ist (noch) klein: zurueckhalten
      break;
    } else {
      raus.push(...block);
    }
  }
  // Der Zeilenumbruch vor der zurueckgehaltenen Tabelle gehoert zum Anfang (sonst schrumpfte der Text).
  const aus = raus.join("\n");
  if (zurueckgehalten && raus.length > 0) return aus + "\n";
  return satzOffen && raus[raus.length - 1] === TABELLE_IM_CHAT ? aus + "\n" : aus;
}
